import { getCustomDateFilterOptions, getRecentDateFilterOptions } from "@components/common/faro-table/faro-table-filter/date-filter/date-filter-utils";
import { getNumberRangeFilterOptions, parseNumberRangeFilterId } from "@components/common/faro-table/faro-table-filter/number-range-filter/number-range-filter-utils";
import { TEAMS_CREATED_FILTER_COLUMN, TEAMS_MEMBERS_FILTER_COLUMN, UNKNOWN_MEMBER_LABEL } from "@components/teams/teams-filters/teams-filter-constants";
import { formatUserRoleType } from "@utils/data-display";

/** Prefix of the ID of the filter for a member, e.g. "member:<identity>" */
const MEMBER_FILTER_ID_PREFIX = "member:";

/** What the filter options of the teams depend on besides the IDs in the URL */

/** What the members filter of the teams applies */

/** A member the teams are filtered by */

/**
 * Returns the filter options of the column. The number ranges, members, dates and date ranges are
 * options of their own, so the ones among the given filter IDs are included.
 */
export function getTeamsFilterOptions(column, filterIds, context) {
  if (column === TEAMS_MEMBERS_FILTER_COLUMN) {
    return {
      ...getNumberRangeFilterOptions(filterIds, getTeamMemberCount),
      ...getTeamsMemberFilterOptions(filterIds, context.teamIdsByMember)
    };
  }
  if (column === TEAMS_CREATED_FILTER_COLUMN) {
    return {
      ...getTeamsCreatedFilterPresets(),
      ...getCustomDateFilterOptions(filterIds, getTeamCreatedTime)
    };
  }
  return getTeamsRoleFilterOptions(context.roles);
}

/**
 * Returns the IDs among the given ones that the column applies, leaving out the ones it does not
 * offer and the repeated ones. The creation date applies only its first filter. The members apply
 * either their first number range or all their members, depending on which comes first, as they
 * filter by one or the other. The roles apply all theirs.
 */
export function getActiveTeamsFilterIds(column, filterIds, context) {
  const options = getTeamsFilterOptions(column, filterIds, context);
  const offeredIds = [...new Set(filterIds)].filter(filterId => filterId in options);
  if (column === TEAMS_CREATED_FILTER_COLUMN) {
    return offeredIds.slice(0, 1);
  }
  if (column === TEAMS_MEMBERS_FILTER_COLUMN) {
    const {
      activeRangeFilterId,
      activeMemberFilterIds
    } = getTeamsMembersFilterState(offeredIds);
    return activeRangeFilterId ? [activeRangeFilterId] : activeMemberFilterIds;
  }
  return offeredIds;
}

/**
 * Returns what the members filter applies among its IDs from the URL. The first valid ID tells
 * whether it filters by a number range or by members: a range is applied alone, while all the
 * members are applied.
 */
export function getTeamsMembersFilterState(filterIds) {
  const validIds = [...new Set(filterIds)].filter(filterId => isNumberRangeFilterId(filterId) || parseTeamMemberFilterId(filterId) !== undefined);
  const [firstId] = validIds;
  if (firstId !== undefined && isNumberRangeFilterId(firstId)) {
    return {
      activeRangeFilterId: firstId,
      activeRange: parseNumberRangeFilterId(firstId),
      activeMemberFilterIds: []
    };
  }
  return {
    activeMemberFilterIds: validIds.filter(filterId => !isNumberRangeFilterId(filterId))
  };
}

/** Returns the filter presets for the recent days the teams were created in */
export function getTeamsCreatedFilterPresets() {
  return getRecentDateFilterOptions(getTeamCreatedTime);
}

/** Returns a filter option for each of the workspace roles, in the given order */
export function getTeamsRoleFilterOptions(roles) {
  return Object.fromEntries(roles.map(role => [role, {
    id: role,
    label: formatUserRoleType(role),
    hasEntityPassedFilter: team => team.role === role
  }]));
}

/**
 * Returns the filter option for the member, which matches the teams the member belongs to. A
 * member whose teams were not looked up yet, or failed to be, matches no team.
 */
export function getTeamsMemberFilterOption({
  identity,
  name
}, teamIdsByMember) {
  return {
    id: getTeamMemberFilterId(identity),
    label: name,
    hasEntityPassedFilter: team => teamIdsByMember[identity]?.teamIds.includes(team.id) ?? false
  };
}

/** Returns the ID of the filter for the member */
export function getTeamMemberFilterId(identity) {
  return `${MEMBER_FILTER_ID_PREFIX}${identity}`;
}

/** Returns the identity of the member of the filter ID, or undefined if it is no member filter */
export function parseTeamMemberFilterId(filterId) {
  if (!filterId.startsWith(MEMBER_FILTER_ID_PREFIX)) {
    return undefined;
  }
  const identity = filterId.slice(MEMBER_FILTER_ID_PREFIX.length);
  return identity.trim() === "" ? undefined : identity;
}

/** Returns the filter options for the members among the IDs, whose names are not known from them */
function getTeamsMemberFilterOptions(filterIds, teamIdsByMember) {
  return Object.fromEntries(filterIds.flatMap(filterId => {
    const identity = parseTeamMemberFilterId(filterId);
    return identity === undefined ? [] : [[filterId, getTeamsMemberFilterOption({
      identity,
      name: UNKNOWN_MEMBER_LABEL
    }, teamIdsByMember)]];
  }));
}

/** Whether the filter ID is a valid number range */
function isNumberRangeFilterId(filterId) {
  return parseNumberRangeFilterId(filterId) !== undefined;
}

/** Returns the number of members of the team, which the members column of the teams table shows */
function getTeamMemberCount(team) {
  return team.memberCount;
}

/** Returns the time the team was created */
function getTeamCreatedTime(team) {
  return new Date(team.createdAt).getTime();
}